import React, { useEffect, useState } from 'react';
import { BriefcaseIcon, ChatBubbleLeftEllipsisIcon, GlobeAltIcon, NewspaperIcon } from '@heroicons/react/24/outline';

import { useTrack } from '@blockworks/platform/services/analytics';
import { ImageWithFallback } from '@blockworks/ui/image-with-fallback';
import { Link } from '@blockworks/ui/legacy/link';

import TwitterIcon from '@/assets/icons/twitter-icon';
import SecondaryButton from '@/components/display/buttons/secondary-button';
import SingleSelectDropdown, { SingleSelectInputOption } from '@/components/display/select/single-select-dropdown';
import GrantsExternalLink from '@/components/pages/grants/components/grant-external-link';
import GrantsHeader from '@/components/pages/grants/components/grants-header';
import SortChevron from '@/components/pages/grants/components/sort-chevron';
import NewsRecirculationSection from '@/components/sections/content/news-recirculation-section';
import { GrantProject, GrantsEntities } from '@/types/pages/api/grants/grants-entities';
import uniq from '@/utils/functions/array/uniq';
import useSortableData from '@/utils/hooks/use-sortable-data';

type Props = {
    grants: GrantProject[];
    ecosystems: GrantsEntities['ecosystemsById'];
    categories: SingleSelectInputOption[];
    grantsTypes: SingleSelectInputOption[];
    title: string;
    counts?: number[];
};

const showCategories = false;

const GrantsProgramsPageLayout = (props: Props) => {
    const { title, categories, grantsTypes, ecosystems, grants, counts } = props;
    const [searchTerm, setSearchTerm] = useState('');
    const [, setPage] = useState(0);
    const [selectedEcosysstem, setSelectedEcosystem] = useState<SingleSelectInputOption | null>(null);
    const [filteredGrants, setFilteredGrants] = useState<GrantProject[]>(grants);
    const [selectedGrantsType, setSelectedGrantsType] = useState<SingleSelectInputOption | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<SingleSelectInputOption | null>(null);
    const { items: sortedAndFiltered, requestSort, sortConfig } = useSortableData<GrantProject>(filteredGrants);
    const track = useTrack();

    const ecosystemOptions: SingleSelectInputOption[] = React.useMemo(() => {
        return uniq(grants.map(g => g.ecosystem).flat(), true).map(id => {
            const es = ecosystems[id]!;
            return { id, name: es?.name, imageUrl: es?.imageUrl };
        });
    }, [grants, ecosystems]);

    useEffect(() => {
        let grantsFilter = grants;

        if (selectedEcosysstem && selectedEcosysstem?.id) {
            grantsFilter = grantsFilter.filter(grant => grant.ecosystem.includes(selectedEcosysstem!.id));
        }

        if (selectedGrantsType && selectedGrantsType?.id) {
            grantsFilter = grantsFilter.filter(grant =>
                grant.grants
                    .flat()
                    .map(g => g.categories)
                    .flat()
                    .includes(selectedGrantsType!.id),
            );
        }

        if (selectedCategory && selectedCategory?.id) {
            grantsFilter = grantsFilter.filter(grant => grant.categories.includes(selectedCategory!.id));
        }

        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            grantsFilter = grantsFilter.filter(grant => {
                const indivGrantsFound =
                    grant.grants.flatMap(g => g?.title?.toLowerCase()).some(g => g?.includes(lowerSearchTerm)) ||
                    grant.grants.flatMap(g => g?.description?.toLowerCase()).some(g => g?.includes(lowerSearchTerm));

                return (
                    grant.title.toLowerCase().includes(lowerSearchTerm) ||
                    grant.assetCode.toLowerCase().includes(lowerSearchTerm) ||
                    indivGrantsFound
                );
            });
        }

        setFilteredGrants(grantsFilter);
    }, [grants, selectedEcosysstem, selectedCategory, selectedGrantsType, searchTerm]);

    return (
        <main className="border-gray-200 w-full">
            <section className="flex flexRow flexWrap xl:flex-nowrap overflow-hidden">
                <div className="h-full p-0 xl:borderR border-b xl:border-b-0 w-full">
                    <div className="flex flex-col space-y-8 p-6 lg:p-10 ">
                        <GrantsHeader
                            breadcrumb={[
                                { title: 'grants', link: '/grants' },
                                { title: 'projects', link: '/grants/programs' },
                            ]}
                            title={title}
                            counts={counts}
                        />
                        <div className="flex flex-col justify-start items-start self-stretch gap-2">
                            <div className="flex flex-col xs:flex-row w-full justify-start items-start self-stretch relative gap-2">
                                <div className="flex-grow w-full md:w-2/4 lg:w-2/3">
                                    <label htmlFor="searchInput" className="text-sm text-left text-gray-700">
                                        Search
                                    </label>
                                    <input
                                        id="searchInput"
                                        type="text"
                                        onChange={ev => {
                                            setSearchTerm(ev.target.value);
                                            setPage(0);
                                        }}
                                        placeholder="Search..."
                                        className="w-full min-w-0 appearance-none rounded-none border border-gray-200 hover:border-gray-300 bg-white py-2.5 px-4 text-sm text-left text-gray-900 placeholder-gray-400 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                    />
                                </div>
                                <div className="flex flex-col justify-start relative gap-1 w-full md:w-2/4 lg:w-1/3">
                                    <SingleSelectDropdown
                                        options={ecosystemOptions}
                                        label="Ecosystem"
                                        placeholder="Select ecosystem to filter"
                                        onChange={setSelectedEcosystem}
                                        value={selectedEcosysstem}
                                        className="min-w-24 "
                                    />
                                </div>
                            </div>
                            <div className="flex justify-start flex-wrap lg:flex-nowrap items-start self-stretch gap-2.5">
                                <div className="flex flex-col justify-start items-start flex-grow gap-1 w-2/4 lg:w-1/3">
                                    <div className="flex flex-col justify-start items-start self-stretch relative gap-1">
                                        <SingleSelectDropdown
                                            options={grantsTypes}
                                            label="Grants Type"
                                            placeholder="Select grants type to filter"
                                            onChange={setSelectedGrantsType}
                                            value={selectedGrantsType}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col justify-start items-start flex-grow gap-1 w-2/4 lg:w-1/3">
                                    <div className="flex flex-col justify-start items-start self-stretch relative gap-1">
                                        <SingleSelectDropdown
                                            options={categories}
                                            label="Project Category"
                                            placeholder="Select grants type to filter"
                                            onChange={setSelectedCategory}
                                            value={selectedCategory}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col w-full">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr className="text-sm text-left uppercase text-[#6a6a6a]">
                                        <td
                                            className="px-1 pb-4 w-4/12 cursor-pointer select-none"
                                            onClick={() => requestSort('title')}
                                        >
                                            <div className="flex gap-1 items-center">
                                                <span>Name</span>
                                                {sortConfig?.key === 'title' && (
                                                    <SortChevron direction={sortConfig.direction} />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-2 pb-4 w-1/12 hidden xs:table-cell">Grants</td>
                                        <td className="px-1 pb-4 w-1/12 hidden xs:table-cell">Website</td>
                                        <td className="px-1 pb-4 w-4/12 hidden md:table-cell">Ecosystem</td>
                                        <td className="px-1 pb-4 w-1/12 hidden lg:table-cell">Bounties</td>
                                        <td className="px-1 pb-4 hidden lg:table-cell">Forum</td>
                                        <td className="px-1 pb-4 hidden lg:table-cell">News</td>
                                        <td className="px-1 pb-4 hidden lg:table-cell">Twitter</td>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {sortedAndFiltered.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="text-light-gray p-4 text-sm">
                                                There were no grants found matching your filter criteria.
                                            </td>
                                        </tr>
                                    )}

                                    {sortedAndFiltered.map(project => (
                                        <tr key={project.title} className="group hover:bg-gray-50">
                                            <td className="px-0 md:px-2 py-3">
                                                <div className="flex justify-between">
                                                    <div className="w-[200pt] md:w-[200pt] flex justify-start flex-nowrap items-center self-stretch relative gap-2">
                                                        {ecosystems[project.assetCode] &&
                                                        ecosystems[project.assetCode]?.imageUrl ? (
                                                            <ImageWithFallback
                                                                src={ecosystems[project.assetCode]?.imageUrl || ''}
                                                                className="w-[25px] h-[25px] rounded-full"
                                                                alt={ecosystems[project.assetCode]?.code || ''}
                                                                fallbackSrc="/images/Blockworks_Meta_Logo.png"
                                                                width={25}
                                                                height={25}
                                                            />
                                                        ) : (
                                                            <ImageWithFallback
                                                                src={project.imageUrl || ''}
                                                                className="w-[25px] h-[25px] rounded-full"
                                                                alt={ecosystems[project.assetCode]?.code || ''}
                                                                fallbackSrc="/images/Blockworks_Meta_Logo.png"
                                                                width={25}
                                                                height={25}
                                                            />
                                                        )}

                                                        <p className="truncate text-base text-[#110b29]">
                                                            {ecosystems[project.assetCode] &&
                                                            project.grants.length > 0 ? (
                                                                <Link
                                                                    href={`/grants?program=${project.assetCode.toLowerCase()}`}
                                                                    className="text-brand hover:text-black"
                                                                >
                                                                    {project.title}
                                                                </Link>
                                                            ) : (
                                                                project.title
                                                            )}
                                                        </p>
                                                    </div>
                                                    <div className="xs:hidden flex justify-between items-center relative gap-2">
                                                        {showCategories && (
                                                            <div className="flex flex-col justify-center items-start self-stretch flex-grow gap-2">
                                                                <div className="flex flex-wrap justify-start items-start gap-1">
                                                                    {project.categories.map(category => (
                                                                        <button
                                                                            key={`${project.title}-${category}`}
                                                                            onClick={ev => {
                                                                                setSelectedCategory({
                                                                                    id: category,
                                                                                    name: category,
                                                                                });
                                                                                ev.stopPropagation();
                                                                            }}
                                                                            className="inline-flex items-center hover:border-gray-300 border border-transparent cursor-pointer bg-gray-100 px-2 py-4.5 py-0.5 text-xs text-gray-700 font-semibold"
                                                                        >
                                                                            {category}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <a
                                                            href={project.websiteUrl}
                                                            target="_blank"
                                                            onClick={() =>
                                                                track('Grants.ClickedWebsite', {
                                                                    project: project.title,
                                                                    assetCode: project.assetCode,
                                                                    url: project.websiteUrl,
                                                                })
                                                            }
                                                            rel="noreferrer"
                                                        >
                                                            <GlobeAltIcon className="h-5 w-5 text-brand-dark hover:text-brand" />
                                                        </a>
                                                        <SecondaryButton
                                                            size="tiny"
                                                            link={project.grantUrl}
                                                            target="_blank"
                                                            onClick={() =>
                                                                track('Grants.ClickedWebsite', {
                                                                    project: project.title,
                                                                    assetCode: project.assetCode,
                                                                    url: project.grantUrl,
                                                                })
                                                            }
                                                        >
                                                            Details
                                                        </SecondaryButton>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="hidden xs:table-cell px-1 py-3">
                                                <SecondaryButton
                                                    size="small"
                                                    link={project.grantUrl}
                                                    target="_blank"
                                                    onClick={() =>
                                                        track('Grants.ClickedWebsite', {
                                                            project: project.title,
                                                            assetCode: project.assetCode,
                                                            url: project.grantUrl,
                                                        })
                                                    }
                                                >
                                                    Details
                                                </SecondaryButton>
                                            </td>
                                            <td className="hidden xs:table-cell px-2 py-3">
                                                <div className="flex items-center w-full">
                                                    <a
                                                        href={project.websiteUrl}
                                                        target="_blank"
                                                        onClick={() =>
                                                            track('Grants.ClickedWebsite', {
                                                                project: project.title,
                                                                assetCode: project.assetCode,
                                                                url: project.websiteUrl,
                                                            })
                                                        }
                                                        rel="noreferrer"
                                                    >
                                                        <GlobeAltIcon className="h-5 w-5 text-brand-dark hover:text-brand" />
                                                    </a>
                                                </div>
                                            </td>
                                            <td className="hidden md:table-cell">
                                                <div className="flex flex-wrap gap-2 p-1 max-w-xs">
                                                    {project.ecosystem.map(ecosystem =>
                                                        ecosystems[ecosystem] ? (
                                                            <span
                                                                key={`${project.title}-${ecosystem}`}
                                                                onClick={ev => {
                                                                    setSelectedEcosystem({
                                                                        id: ecosystem,
                                                                        name: ecosystem,
                                                                        imageUrl: ecosystems[ecosystem]?.imageUrl ?? '',
                                                                    });
                                                                    ev.stopPropagation();
                                                                }}
                                                                className="h-[24px] w-[24px] flex overflow-hidden cursor-pointer rounded-full border-2 border-white outline outline-1 outline-offset-0 outline-brand hover:outline-black"
                                                            >
                                                                <ImageWithFallback
                                                                    src={ecosystems[ecosystem]!.imageUrl}
                                                                    alt={ecosystems[ecosystem]!.title}
                                                                    width={20}
                                                                    height={20}
                                                                    className="hover:grayscale rounded-full grayscale-0 object-contain"
                                                                />
                                                            </span>
                                                        ) : (
                                                            <span
                                                                key={`${project.title}-${ecosystem}`}
                                                                className="inline-flex items-center rounded bg-purple-100 px-2 py-4 text-xs font-medium text-purple-800 cursor-default"
                                                            >
                                                                {ecosystem}
                                                            </span>
                                                        ),
                                                    )}
                                                </div>
                                            </td>
                                            <td className="hidden lg:table-cell px-2">
                                                <div className="flex flex-col justify-center items-start self-stretch flex-grow gap-2">
                                                    <div className="flex justify-start items-start self-stretch relative gap-1">
                                                        <GrantsExternalLink
                                                            href={project.bugBountyUrl}
                                                            text={
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    strokeWidth={1.5}
                                                                    stroke="currentColor"
                                                                    className="w-5 h-5 hover:text-brand"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44.125 2.104.52 4.136 1.153 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 00-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.734 3.734 0 01.4-2.253M12 8.25a2.25 2.25 0 00-2.248 2.146M12 8.25a2.25 2.25 0 012.248 2.146M8.683 5a6.032 6.032 0 01-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0115.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 00-.575-1.752M4.921 6a24.048 24.048 0 00-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 01-5.223 1.082"
                                                                    />
                                                                </svg>
                                                            }
                                                            hideExternalLinkIcon
                                                        />
                                                        <div className="flex justify-start items-start self-stretch relative gap-1">
                                                            <GrantsExternalLink
                                                                href={project.bountyUrl}
                                                                text={
                                                                    <BriefcaseIcon className="w-5 h-5 hover:text-brand" />
                                                                }
                                                                hideExternalLinkIcon
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="hidden lg:table-cell px-2">
                                                <GrantsExternalLink
                                                    href={project.governanceForumUrl}
                                                    text={
                                                        <ChatBubbleLeftEllipsisIcon className="w-5 h-5 hover:text-brand" />
                                                    }
                                                    hideExternalLinkIcon
                                                />
                                            </td>
                                            <td className="hidden lg:table-cell px-2">
                                                <GrantsExternalLink
                                                    href={project.blogUrl}
                                                    text={<NewspaperIcon className="w-5 h-5 hover:text-brand" />}
                                                    hideExternalLinkIcon
                                                />
                                            </td>
                                            <td className="hidden lg:table-cell px-2">
                                                <GrantsExternalLink
                                                    href={project.twitterUrl}
                                                    text={
                                                        <TwitterIcon
                                                            className="h-5 w-5 hover:text-brand"
                                                            style="primary"
                                                        />
                                                    }
                                                    hideExternalLinkIcon
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <NewsRecirculationSection
                        algoliaSearchKeyword="grants"
                        title="grants in the news"
                        count={4}
                        paddingClassName="p-6"
                        className="p-0"
                    />
                </div>
            </section>
        </main>
    );
};

export default GrantsProgramsPageLayout;
